import React, { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { isAttachUiVisible } from '@enuan/chat__utils';
import { SubmitForm } from '../../submitForm/SubmitForm';
import { configurationContext } from '../../../contexts/configuration';
import { chatFrameMessages } from './i18n';
import { Frame } from 'src/components/UI/Frame/Frame';
import { styled } from '@mui/material/styles';
import { ReconnectChat } from 'src/components/reconnectChat';
import { MessagesList } from './MessagesList';
import { ScrollToUnread } from './ScrollToUnread';
import { WritingMessage } from './WritingMessage';
import { MessagesListRefsProvider } from './messagesListRefs.context';
import { useFileDrag } from '../../submitForm/attach/hooks';
import { FileDragContext } from '../../submitForm/attach/dragContext';

/**
 * Root chat view: renders the `Frame` with header, messages body, and the
 * `SubmitForm` footer.
 *
 * Also sets up frame-level file-drag detection via {@link useFileDrag} and
 * publishes the result through {@link FileDragContext} so that `FormWithDrop`
 * (deep inside the footer) can display the drop-zone overlay as soon as a
 * file drag enters any part of the chat frame — not only when the pointer is
 * directly over the form.
 */
export const ChatMessages = styled(
	({ className }: { className?: string }) => {
		const { chat, file_upload, attach_ui } = useContext(configurationContext);
		const { logo } = chat ?? {};
		const intl = useIntl();

		// Detect file drags over the entire frame so the footer overlay activates
		// even when the pointer is over the messages area. Gated by both the
		// backend `file_upload` flag and the UI-only `attach_ui` flag.
		const { isDraggingOver, dragHandlers } = useFileDrag({
			enabled: !!file_upload?.enabled && isAttachUiVisible({ attach_ui }),
		});

		const header = useMemo(
			() => ({
				title: intl.formatMessage(chatFrameMessages.header_title),
				description: intl.formatMessage(chatFrameMessages.header_description),
				logo,
			}),
			[intl, logo],
		);

		const fileDragContextValue = useMemo(
			() => ({ isDraggingFileOver: isDraggingOver }),
			[isDraggingOver],
		);

		return (
			<FileDragContext.Provider value={fileDragContextValue}>
				<MessagesListRefsProvider>
					<Frame
						{...dragHandlers}
						className={className}
						header={header}
						body={
							<>
								<MessagesList />
								<WritingMessage className="chat__info" />
								<ScrollToUnread />
								<ReconnectChat className="chat__info" />
							</>
						}
						footer={<SubmitForm />}
					/>
				</MessagesListRefsProvider>
			</FileDragContext.Provider>
		);
	},
	{ label: 'ChatMessages', target: 'ChatMessages-root' },
)({
	'& .chat__main': {
		flexDirection: 'column',
		overflow: 'hidden',
		flex: 1,
		display: 'flex',
		padding: 0,
	},
	'& .messages-list': {
		flex: 1,
		'& .message': {
			marginBottom: '0.175em',
			'&.message--right': {
				'&:not(.message--first) .message__balloon': {
					borderTopRightRadius: 0,
				},
				'&:not(.message--last)': {
					'&:not(.message--sending):not(.message--selected) .message__footer': {
						display: 'none',
					},
					'& .message__header, & .message__avatar': {
						display: 'none',
					},
				},
				'& .message__footer': {
					flexDirection: 'row-reverse',
				},
			},
			'&.message--left': {
				'&:not(.message--first) .message__header, &:not(.message--first) .message__avatar':
					{
						display: 'none',
					},
				'&:not(.message--last)': {
					'&.message__footer': {
						display: 'none',
					},
					'& .message__balloon': {
						borderBottomLeftRadius: 0,
					},
				},
				'&.message--last': {
					marginBottom: 'calc(0.175em * 2)',
				},
				'& + .message--right': {
					marginBottom: 'calc(-30px * 0.5)',
				},
				'& + .message--form': {
					marginBottom: '1em',
				},
			},
			'&.message--newest': {
				marginBottom: '1em',
			},
			'&.message--oldest': {
				marginTop: '3em',
			},
			'&.message--read:not(.message--last):not(.message--sending):not(.message--selected) .message__footer':
				{
					display: 'none',
				},
			'&:not(.message--last):not(.message--selected) .text__footer:not(.placeholder)':
				{
					display: 'none',
				},
			'&.message--first .message__balloon': {
				borderTopRightRadius: '0.6em',
			},
			'&.message--last .message__balloon': {
				borderBottomLeftRadius: '0.6em',
			},
			'&.message--survey, &.message--summary, &.message--carousel, &.message--form':
				{
					'& + .message--right': {
						marginBottom: '1em',
					},
				},
			'&.message--carousel.message--last': {
				marginBottom: '2em',
			},
		},
	},
	'& .breakline': {
		padding: '0.175em calc(20px + 15px) 0.5em calc(20px * 2)',
	},
	'& .chat__info': {
		fontWeight: 'bold',
		padding: '0.175em calc(20px + 15px) 0.5em calc(20px * 2)',
	},
});
